import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Building2, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * Admin-only Rooms & Facilities manager. Two-column layout: facilities on the
 * left, spaces inside the selected facility on the right. Deleting a facility
 * is refused server-side while any space still references it, so the UI
 * mirrors that by disabling the button once spaces exist.
 */
export default function SpaceManager({ embedded }) {
  const { user } = useAuth();
  const canManage = user.role === "org_admin" || user.role === "super_admin";
  const canDelete = user.role === "super_admin";
  const [facilities, setFacilities] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openFac, setOpenFac] = useState(false);
  const [facForm, setFacForm] = useState({ id: null, name: "", address: "", notes: "", color: "#F59E0B" });
  const [openSpc, setOpenSpc] = useState(false);
  const [spcForm, setSpcForm] = useState({ id: null, facility_id: null, name: "", capacity: "", color: "#F59E0B", notes: "" });

  const load = async () => {
    const [f, s] = await Promise.all([api.get("/facilities"), api.get("/spaces")]);
    setFacilities(f.data);
    setSpaces(s.data);
    if (!selected && f.data[0]) setSelected(f.data[0].id);
  };
  useEffect(() => { load(); }, []);

  const openFacilityForm = (f) => {
    setFacForm(f
      ? { id: f.id, name: f.name, address: f.address || "", notes: f.notes || "", color: f.color || "#F59E0B" }
      : { id: null, name: "", address: "", notes: "", color: "#F59E0B" });
    setOpenFac(true);
  };
  const saveFacility = async () => {
    if (!facForm.name.trim()) return toast.error("Name is required");
    try {
      const body = { name: facForm.name, address: facForm.address, notes: facForm.notes, color: facForm.color };
      if (facForm.id) await api.patch(`/facilities/${facForm.id}`, body);
      else await api.post("/facilities", body);
      toast.success("Saved");
      setOpenFac(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeFacility = async (id) => {
    if (!confirm("Delete this facility?")) return;
    try {
      await api.delete(`/facilities/${id}`);
      if (selected === id) setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); }
  };

  const openSpaceForm = (s) => {
    setSpcForm(s
      ? { id: s.id, facility_id: s.facility_id, name: s.name, capacity: s.capacity ?? "", color: s.color || "#F59E0B", notes: s.notes || "" }
      : { id: null, facility_id: selected, name: "", capacity: "", color: "#F59E0B", notes: "" });
    setOpenSpc(true);
  };
  const saveSpace = async () => {
    if (!spcForm.name.trim()) return toast.error("Name is required");
    if (!spcForm.facility_id) return toast.error("Pick a facility");
    try {
      const body = {
        facility_id: spcForm.facility_id,
        name: spcForm.name,
        capacity: spcForm.capacity === "" ? null : Number(spcForm.capacity),
        color: spcForm.color,
        notes: spcForm.notes,
      };
      if (spcForm.id) await api.patch(`/spaces/${spcForm.id}`, body);
      else await api.post("/spaces", body);
      toast.success("Saved");
      setOpenSpc(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeSpace = async (id) => {
    if (!confirm("Delete this room? Any future rehearsals will lose their room assignment.")) return;
    await api.delete(`/spaces/${id}`);
    load();
  };

  const spacesForSelected = spaces.filter((s) => s.facility_id === selected);

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"} data-testid="space-manager">
      {!embedded && (
        <header>
          <h1 className="font-display text-3xl tracking-tight font-light">Rooms & Facilities</h1>
          <p className="text-zinc-400 text-sm mt-1">Physical spaces available for rehearsal.</p>
        </header>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Facilities column */}
        <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Facilities</div>
            {canManage && (
              <button data-testid="add-facility-btn" onClick={() => openFacilityForm(null)} className="text-gold hover:bg-gold/10 rounded p-1">
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {facilities.length === 0 && (
            <div className="text-xs text-zinc-500 italic py-6 text-center">
              No facilities yet. Add one to start scheduling rooms.
            </div>
          )}
          <ul className="space-y-1">
            {facilities.map((f) => (
              <li key={f.id}>
                <button
                  data-testid={`facility-item-${f.id}`}
                  onClick={() => setSelected(f.id)}
                  className={`w-full flex items-center gap-2 px-2 py-2 rounded text-left text-sm transition-colors
                    ${selected === f.id ? "bg-gold/10 text-gold border-l-2 border-gold" : "hover:bg-white/5 border-l-2 border-transparent"}`}
                >
                  <Building2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-[10px] text-zinc-500">{spaces.filter((s) => s.facility_id === f.id).length}</span>
                </button>
                {canManage && selected === f.id && (
                  <div className="flex items-center gap-1 mt-1 pl-6">
                    <button onClick={() => openFacilityForm(f)} className="text-[11px] text-zinc-400 hover:text-gold inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                    {canDelete && (
                      <button onClick={() => removeFacility(f.id)} className="text-[11px] text-zinc-400 hover:text-red-400 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Spaces column */}
        <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">
              Rooms in {facilities.find((f) => f.id === selected)?.name || "…"}
            </div>
            {canManage && selected && (
              <button data-testid="add-space-btn" onClick={() => openSpaceForm(null)} className="text-gold hover:bg-gold/10 rounded p-1">
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {!selected && (
            <div className="text-xs text-zinc-500 italic py-8 text-center">Pick a facility to see its rooms.</div>
          )}
          {selected && spacesForSelected.length === 0 && (
            <div className="text-xs text-zinc-500 italic py-8 text-center">
              No rooms yet in this facility.
            </div>
          )}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {spacesForSelected.map((s) => (
              <li key={s.id}
                  className="rounded-md border border-white/10 bg-[#121212] p-3 relative group"
                  style={{ borderLeft: `3px solid ${s.color || "#F59E0B"}` }}
                  data-testid={`space-item-${s.id}`}>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{s.name}</div>
                    {s.capacity && <div className="text-[11px] text-zinc-500">Capacity {s.capacity}</div>}
                    {s.notes && <div className="text-[11px] text-zinc-400 mt-1 line-clamp-2">{s.notes}</div>}
                  </div>
                </div>
                {canManage && (
                  <div className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 flex gap-1 transition-opacity">
                    <button onClick={() => openSpaceForm(s)} className="text-zinc-400 hover:text-gold p-1"><Edit3 className="w-3 h-3" /></button>
                    {canDelete && (
                      <button onClick={() => removeSpace(s.id)} className="text-zinc-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Facility dialog */}
      <Dialog open={openFac} onOpenChange={setOpenFac}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{facForm.id ? "Edit facility" : "Add facility"}</DialogTitle>
            <DialogDescription className="text-zinc-500">A physical building — a theatre, a studio, a warehouse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="facility-name-input" value={facForm.name} onChange={(e) => setFacForm({ ...facForm, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            <div><Label>Address</Label><Input value={facForm.address} onChange={(e) => setFacForm({ ...facForm, address: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={facForm.notes} onChange={(e) => setFacForm({ ...facForm, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            <div><Label>Color</Label><input type="color" value={facForm.color} onChange={(e) => setFacForm({ ...facForm, color: e.target.value })} className="h-9 w-full rounded bg-[#0A0A0A] border border-white/10" /></div>
          </div>
          <DialogFooter>
            <button data-testid="save-facility-btn" onClick={saveFacility} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Space dialog */}
      <Dialog open={openSpc} onOpenChange={setOpenSpc}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{spcForm.id ? "Edit room" : "Add room"}</DialogTitle>
            <DialogDescription className="text-zinc-500">A single bookable space inside a facility.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Facility</Label>
              <select
                value={spcForm.facility_id || ""}
                onChange={(e) => setSpcForm({ ...spcForm, facility_id: e.target.value })}
                className="w-full h-9 rounded-md bg-[#0A0A0A] border border-white/10 text-sm px-2"
                data-testid="space-facility-select"
              >
                <option value="">— Pick facility —</option>
                {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div><Label>Name</Label><Input data-testid="space-name-input" value={spcForm.name} onChange={(e) => setSpcForm({ ...spcForm, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            <div><Label>Capacity (people)</Label><Input type="number" min="0" value={spcForm.capacity} onChange={(e) => setSpcForm({ ...spcForm, capacity: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            <div><Label>Color</Label><input type="color" value={spcForm.color} onChange={(e) => setSpcForm({ ...spcForm, color: e.target.value })} className="h-9 w-full rounded bg-[#0A0A0A] border border-white/10" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={spcForm.notes} onChange={(e) => setSpcForm({ ...spcForm, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          </div>
          <DialogFooter>
            <button data-testid="save-space-btn" onClick={saveSpace} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
